import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faArrowLeftFromLine } from '@fortawesome/pro-regular-svg-icons/faArrowLeftFromLine';
import { faMessage } from '@fortawesome/pro-regular-svg-icons/faMessage';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import { faSidebar } from '@fortawesome/pro-regular-svg-icons/faSidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocationPathname, useNavigate, useParams } from '@rocketlane/rocket-router';
import { Tooltip } from '@rocketlane/ui';
import { useCallback, useMemo, useState } from 'react';
import { NITRO_CLAW_AGENT_NAME } from 'api/nitro-claw';
import { AppShellSwitcher } from 'components/organisms/app-shell-switcher';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate } from 'hooks/use-translation';
import { useAgentChatHistoryInfiniteQuery } from 'queries/ai-chat/ai-chat';
import { usePending } from 'stores/notifications';
import { isTauriApp } from 'utils/is-tauri';
import nitroClawLogo from './assets/nitro-claw-logo.svg';
import { AGENT_ITEMS, buildPrimaryNavItems, RESOURCE_NAV_ITEMS, ROUTE_BASE, RESERVED_SEGMENTS } from './nitro-claw-desktop-sidebar.config';
import { NitroClawDesktopSidebarFooter } from './nitro-claw-desktop-sidebar-footer';
import { AgentIcon, BrandBlock, BrandLogo, BrandTitle, ChatsEmpty, CollapseButton, CollapsedBrandHitArea, CountBadge, HeaderRow, NavIcon, NavLabel, NavRow, NavSection, SectionLabel, ShowMoreButton, SidebarRoot, SidebarScroll } from './nitro-claw-desktop-sidebar.styles';
const INITIAL_VISIBLE_CHATS = 6;
const VISIBLE_CHATS_STEP = 6;
const MaybeTooltip = ({ tip, children })=>{
    if (!tip) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    return /*#__PURE__*/ _jsx(Tooltip, {
        content: tip,
        side: "right",
        children: children
    });
};
/** Web-only: brand opens Rocketlane ↔ Nitro Claw switcher. Tauri stays Nitro Claw. */ const MaybeAppShellSwitcher = ({ fill, children })=>{
    if (isTauriApp()) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    return /*#__PURE__*/ _jsx(AppShellSwitcher, {
        placement: "bottomLeft",
        fill: fill,
        children: children
    });
};
export function NitroClawDesktopSidebar() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const pathname = useLocationPathname();
    const { conversationId } = useParams();
    const [uiPreference, setUiPreference] = useLocalStorage('nitro-claw-desktop-sidebar', {
        collapsed: false
    });
    const collapsed = Boolean(uiPreference.collapsed);
    const [pending] = usePending();
    const [visibleChatCount, setVisibleChatCount] = useState(INITIAL_VISIBLE_CHATS);
    const { data, isLoading } = useAgentChatHistoryInfiniteQuery({
        limit: 40
    }, NITRO_CLAW_AGENT_NAME, {
        enabled: !collapsed
    });
    const conversations = useMemo(()=>{
        var _ref;
        const pages = (_ref = data === null || data === void 0 ? void 0 : data.pages) !== null && _ref !== void 0 ? _ref : [];
        return pages.flatMap((page)=>{
            var _page_conversations;
            return (_page_conversations = page.conversations) !== null && _page_conversations !== void 0 ? _page_conversations : [];
        }).slice(0, 20);
    }, [
        data === null || data === void 0 ? void 0 : data.pages
    ]);
    const visibleConversations = useMemo(()=>conversations.slice(0, visibleChatCount), [
        conversations,
        visibleChatCount
    ]);
    const hasMoreConversations = conversations.length > visibleChatCount;
    const showMoreConversations = useCallback(()=>{
        setVisibleChatCount((prev)=>Math.min(prev + VISIBLE_CHATS_STEP, conversations.length));
    }, [
        conversations.length
    ]);
    const activeConversationId = conversationId && !RESERVED_SEGMENTS.has(conversationId) ? conversationId : null;
    const primaryItems = useMemo(()=>buildPrimaryNavItems(pending), [
        pending
    ]);
    const toggleCollapsed = useCallback(()=>{
        setUiPreference((prev)=>({
                ...prev,
                collapsed: !prev.collapsed
            }));
    }, [
        setUiPreference
    ]);
    const go = useCallback((path)=>{
        navigate(path);
    }, [
        navigate
    ]);
    const label = useCallback((id, defaultMessage)=>translate(id, undefined, defaultMessage), [
        translate
    ]);
    const collapseTooltip = collapsed ? label('nitro_claw.sidebar.expand', 'Expand sidebar') : label('nitro_claw.sidebar.collapse', 'Collapse sidebar');
    return /*#__PURE__*/ _jsxs(SidebarRoot, {
        $collapsed: collapsed,
        "data-cy": "nitro-claw-desktop-sidebar",
        "aria-label": label('nitro_claw.sidebar.title', 'Nitro Claw'),
        children: [
            /*#__PURE__*/ _jsx(HeaderRow, {
                $collapsed: collapsed,
                children: collapsed ? /*#__PURE__*/ _jsxs(CollapsedBrandHitArea, {
                    "data-cy": "nitro-claw-desktop-sidebar.collapsed-brand",
                    children: [
                        /*#__PURE__*/ _jsx(MaybeAppShellSwitcher, {
                            children: /*#__PURE__*/ _jsx(BrandLogo, {
                                src: nitroClawLogo,
                                alt: "",
                                width: 20,
                                height: 20,
                                "data-cy": "nitro-claw-desktop-sidebar.logo"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Tooltip, {
                            content: collapseTooltip,
                            side: "right",
                            children: /*#__PURE__*/ _jsx(CollapseButton, {
                                type: "button",
                                onClick: toggleCollapsed,
                                "aria-label": collapseTooltip,
                                "data-cy": "nitro-claw-desktop-sidebar.collapse",
                                children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                    icon: faSidebar
                                })
                            })
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(MaybeAppShellSwitcher, {
                            fill: true,
                            children: /*#__PURE__*/ _jsxs(BrandBlock, {
                                children: [
                                    /*#__PURE__*/ _jsx(BrandLogo, {
                                        src: nitroClawLogo,
                                        alt: "",
                                        width: 20,
                                        height: 20,
                                        "data-cy": "nitro-claw-desktop-sidebar.logo"
                                    }),
                                    /*#__PURE__*/ _jsx(BrandTitle, {
                                        children: label('nitro_claw.sidebar.title', 'Nitro Claw')
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Tooltip, {
                            content: collapseTooltip,
                            side: "right",
                            children: /*#__PURE__*/ _jsx(CollapseButton, {
                                type: "button",
                                onClick: toggleCollapsed,
                                "aria-label": collapseTooltip,
                                "data-cy": "nitro-claw-desktop-sidebar.collapse",
                                children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                    icon: faArrowLeftFromLine
                                })
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(SidebarScroll, {
                $collapsed: collapsed,
                children: [
                    /*#__PURE__*/ _jsx(NavSection, {
                        $collapsed: collapsed,
                        children: primaryItems.map((item)=>/*#__PURE__*/ _jsx(MaybeTooltip, {
                                tip: collapsed ? label(item.labelKey, item.defaultMessage) : undefined,
                                children: /*#__PURE__*/ _jsxs(NavRow, {
                                    type: "button",
                                    $active: item.match(pathname),
                                    onClick: ()=>go(item.path),
                                    "data-cy": `nitro-claw-desktop-sidebar.${item.id}`,
                                    children: [
                                        /*#__PURE__*/ _jsx(NavIcon, {
                                            children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                icon: item.icon
                                            })
                                        }),
                                        !collapsed ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(NavLabel, {
                                                    children: label(item.labelKey, item.defaultMessage)
                                                }),
                                                item.badge != null && item.badge > 0 ? /*#__PURE__*/ _jsx(CountBadge, {
                                                    children: item.badge
                                                }) : null
                                            ]
                                        }) : null
                                    ]
                                })
                            }, item.id))
                    }),
                    /*#__PURE__*/ _jsxs(NavSection, {
                        $collapsed: collapsed,
                        children: [
                            !collapsed ? /*#__PURE__*/ _jsx(SectionLabel, {
                                "data-cy": "nitro-claw-desktop-sidebar.chats-label",
                                children: label('nitro_claw.sidebar.chats', 'Chats')
                            }) : null,
                            /*#__PURE__*/ _jsx(MaybeTooltip, {
                                tip: collapsed ? label('nitro_claw.sidebar.new_chat', 'New chat') : undefined,
                                children: /*#__PURE__*/ _jsxs(NavRow, {
                                    type: "button",
                                    $active: pathname === ROUTE_BASE || pathname === `${ROUTE_BASE}/`,
                                    onClick: ()=>go(ROUTE_BASE),
                                    "data-cy": "nitro-claw-desktop-sidebar.new-chat",
                                    children: [
                                        /*#__PURE__*/ _jsx(NavIcon, {
                                            children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                icon: faPenToSquare
                                            })
                                        }),
                                        !collapsed ? /*#__PURE__*/ _jsx(NavLabel, {
                                            children: label('nitro_claw.sidebar.new_chat', 'New chat')
                                        }) : null
                                    ]
                                })
                            }),
                            !collapsed ? visibleConversations.map((conv)=>{
                                var _conv_title;
                                const title = ((_conv_title = conv.title) === null || _conv_title === void 0 ? void 0 : _conv_title.trim()) || label('nitro_claw.sidebar.untitled_chat', 'Untitled conversation');
                                return /*#__PURE__*/ _jsxs(NavRow, {
                                    type: "button",
                                    $active: activeConversationId === conv.conversation_id,
                                    onClick: ()=>go(`${ROUTE_BASE}/${conv.conversation_id}`),
                                    "data-cy": `nitro-claw-desktop-sidebar.chat.${conv.conversation_id}`,
                                    children: [
                                        /*#__PURE__*/ _jsx(NavIcon, {
                                            children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                icon: faMessage
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(NavLabel, {
                                            title: title,
                                            children: title
                                        })
                                    ]
                                }, conv.conversation_id);
                            }) : null,
                            !collapsed && hasMoreConversations ? /*#__PURE__*/ _jsx(ShowMoreButton, {
                                type: "button",
                                onClick: showMoreConversations,
                                "data-cy": "nitro-claw-desktop-sidebar.chats-show-more",
                                children: label('show_more', 'Show more')
                            }) : null,
                            !collapsed && !isLoading && conversations.length === 0 ? /*#__PURE__*/ _jsx(ChatsEmpty, {
                                children: label('nitro_claw.sidebar.chats_empty', 'No conversations yet')
                            }) : null
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(NavSection, {
                        $collapsed: collapsed,
                        children: [
                            !collapsed ? /*#__PURE__*/ _jsx(SectionLabel, {
                                "data-cy": "nitro-claw-desktop-sidebar.resources-label",
                                children: label('nitro_claw.sidebar.resources', 'Resources')
                            }) : null,
                            RESOURCE_NAV_ITEMS.map((item)=>/*#__PURE__*/ _jsx(MaybeTooltip, {
                                    tip: collapsed ? label(item.labelKey, item.defaultMessage) : undefined,
                                    children: /*#__PURE__*/ _jsxs(NavRow, {
                                        type: "button",
                                        $active: item.match(pathname),
                                        onClick: ()=>go(item.path),
                                        "data-cy": `nitro-claw-desktop-sidebar.${item.id}`,
                                        children: [
                                            /*#__PURE__*/ _jsx(NavIcon, {
                                                children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                    icon: item.icon
                                                })
                                            }),
                                            !collapsed ? /*#__PURE__*/ _jsx(NavLabel, {
                                                children: label(item.labelKey, item.defaultMessage)
                                            }) : null
                                        ]
                                    })
                                }, item.id))
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(NavSection, {
                        $collapsed: collapsed,
                        children: [
                            !collapsed ? /*#__PURE__*/ _jsx(SectionLabel, {
                                "data-cy": "nitro-claw-desktop-sidebar.agents-label",
                                children: label('nitro_claw.sidebar.agents', 'Agents')
                            }) : null,
                            AGENT_ITEMS.map((item)=>/*#__PURE__*/ _jsx(MaybeTooltip, {
                                    tip: collapsed ? label(item.labelKey, item.defaultMessage) : undefined,
                                    children: /*#__PURE__*/ _jsxs(NavRow, {
                                        type: "button",
                                        $active: item.match(pathname),
                                        onClick: ()=>go(item.path),
                                        "data-cy": `nitro-claw-desktop-sidebar.agent.${item.id}`,
                                        children: [
                                            /*#__PURE__*/ _jsx(AgentIcon, {
                                                src: item.iconSrc,
                                                alt: "",
                                                width: 16,
                                                height: 16
                                            }),
                                            !collapsed ? /*#__PURE__*/ _jsx(NavLabel, {
                                                children: label(item.labelKey, item.defaultMessage)
                                            }) : null
                                        ]
                                    })
                                }, item.id))
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(NitroClawDesktopSidebarFooter, {
                collapsed: collapsed
            })
        ]
    });
}
